import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, CenteredContainer, Link, Text } from '@nerdwallet/currency';

import nds from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import { itemsLinks } from './data/brand-data';
import styles from './sticky-header-v2.module.less';

import {
  stringInputSpec,
  boolCheckboxSpec,
  repeatable,
  stringSelectSpec,
  group,
} from '../../lib/editing-configs';

const stickyHeaderLayoutStyles = {
  BOA2023: 'boa2023',
  SEM: 'sem',
  BOA2024: 'boa2024', // TODO: change when BOA 2024 style is defined
};

const outlinedLinks = (item) => (
  <Box className={styles.stickyHeaderLinkItem}>
    <Link className={styles.link} href={item?.url || '#'}>
      {item?.name}
    </Link>
  </Box>
);

const buttonLinks = (item) => (
  <Box id={item?.name} className={styles.stickyHeaderButtonItem}>
    <Link href={item?.url || '#'}>
      <Text bold size="x-small" component="a" color="neutral-dark">
        {item?.name}
      </Text>
    </Link>
  </Box>
);

const renderContent = (menuItems, layout, centerInParentContainer) => {
  const menuItemsComponent = {
    [stickyHeaderLayoutStyles.BOA2023]: buttonLinks,
    [stickyHeaderLayoutStyles.SEM]: outlinedLinks,
    [stickyHeaderLayoutStyles.BOA2024]: buttonLinks,
  };

  const menuItemsComponentStyle = classNames([
    styles.stickyHeaderItems,
    {
      [styles.stickyHeaderLinkItems]: layout === stickyHeaderLayoutStyles.SEM,
    },
  ]);

  if (centerInParentContainer) {
    return (
      <CenteredContainer
        className={menuItemsComponentStyle}
        data-testid="sticky-header-items-container"
      >
        {menuItems.map(menuItemsComponent[layout])}
      </CenteredContainer>
    );
  }

  return (
    <Box
      className={menuItemsComponentStyle}
      data-testid="sticky-header-items-container"
    >
      {menuItems.map(menuItemsComponent[layout])}
    </Box>
  );
};

const StickyHeader = ({
  sections,
  usePreConfiguredNav,
  alignment,
  centerInParent,
  stickyHeaderLayout,
  container,
}) => {
  const menuItems = usePreConfiguredNav ? itemsLinks : sections;
  const stickyHeaderRef = useRef();
  const [stickyToTop, setStickyToTop] = useState(false);

  function onScroll() {
    const shouldStickyOnTop =
      stickyHeaderRef.current.getBoundingClientRect().top <= 0;
    setStickyToTop(shouldStickyOnTop);
  }

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Sticky header should have a higher z-index, this makes editor panel tab hidden
  useEffect(() => {
    const stickyHeaderControl = document.querySelector(
      'div.editor-control__type.editor-control__type__stickyheaderv2'
    );
    if (stickyHeaderControl) {
      // if panel tab is in the page we make it visible over sticky header
      stickyHeaderControl.style.zIndex = 102;
    }
  }, []);

  return (
    <Box
      ref={stickyHeaderRef}
      id="sticky-header-v2"
      data-testid="sticky-header-v2"
      className={classNames([
        styles.stickyHeader,
        {
          [styles.disableDropShadow]: !stickyToTop,
        },
        nds(`justify-content-${alignment}`),
      ])}
    >
      <UniversalContainer {...container}>
        {renderContent(menuItems, stickyHeaderLayout, centerInParent)}
      </UniversalContainer>
    </Box>
  );
};

StickyHeader.propTypes = {
  brandLogoUrl: PropTypes.string,
  awardsYear: PropTypes.string,
  usePreConfiguredNav: PropTypes.bool,
  shouldTrack: PropTypes.bool,
  alignment: PropTypes.string,
  centerInParent: PropTypes.bool,
  stickyHeaderLayout: PropTypes.string,
  container: universalContainerPropTypes,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ),
};

StickyHeader.defaultProps = {
  alignment: 'center',
  brandLogoUrl: '',
  awardsYear: '2023',
  sections: [],
  usePreConfiguredNav: false,
  shouldTrack: false,
  centerInParent: false,
  stickyHeaderLayout: stickyHeaderLayoutStyles.BOA2023,
};

StickyHeader.getEditingConfig = (componentDocument) => {
  const sectionConfig = !componentDocument?.props?.usePreConfiguredNav
    ? [
        repeatable('sections', 'Sections', 'Section', () => {
          return [
            stringInputSpec('name', 'Name'),
            stringInputSpec('url', 'Section URL'),
          ];
        }),
      ]
    : [];

  const awardsYearConfig = componentDocument?.props?.usePreConfiguredNav
    ? [
        stringSelectSpec('awardsYear', 'Awards Year', [
          { label: '2023', value: '2023' },
        ]),
      ]
    : [];

  return [
    boolCheckboxSpec('usePreConfiguredNav', 'Use pre-configured navigation?'),
    ...awardsYearConfig,
    stringSelectSpec('stickyHeaderLayout', 'Header button style', [
      { label: 'BOA 2023 style', value: stickyHeaderLayoutStyles.BOA2023 },
      { label: 'SEM page style', value: stickyHeaderLayoutStyles.SEM },
      { label: 'BOA 2024 style', value: stickyHeaderLayoutStyles.BOA2024 },
    ]),
    group('Container', [
      boolCheckboxSpec('centerInParent', 'Center In Parent?'),
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Container',
        ['hide'],
        'container',
        {
          disableGroup: true,
        }
      ),
    ]),
    group('Header Settings', [
      stringSelectSpec(
        'alignment',
        'Alignment',
        [
          { label: 'Left', value: 'flex-start' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'flex-end' },
        ],
        { help: 'Defaults to "Center"' }
      ),
    ]),
    ...sectionConfig,
  ];
};

StickyHeader.displayName = 'StickyHeader';

export default StickyHeader;
